/** Save state of the sheet's annotations: "Allt vistað" / "Án nets – vistast síðar" / "N óvistað". Tap = save now. */
import { CloudCheck, CloudOff, CloudUpload, LoaderCircle, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { SheetAnnotations } from '../annotations/contract';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { useOnline } from '../lib/useOnline';

export function syncView(sync: SheetAnnotations['sync'], online: boolean): { key: 'saved' | 'offline' | 'pending' | 'saving' | 'error'; count: number } {
  if (sync.status === 'error') return { key: 'error', count: sync.pending };
  if (sync.pending > 0 && (sync.status === 'offline' || !online)) return { key: 'offline', count: sync.pending };
  if (sync.pending > 0) return { key: sync.status === 'syncing' ? 'saving' : 'pending', count: sync.pending };
  return { key: 'saved', count: 0 };
}

const ICONS: Record<ReturnType<typeof syncView>['key'], LucideIcon> = {
  saved: CloudCheck,
  offline: CloudOff,
  pending: CloudUpload,
  saving: LoaderCircle,
  error: TriangleAlert,
};

/** compact: the label only from sm up; iconOnly: never shown (screen readers and the tooltip keep it). */
export function SyncIndicator({ sync, compact, iconOnly }: { sync: SheetAnnotations['sync']; compact?: boolean; iconOnly?: boolean }) {
  const t = useT();
  const online = useOnline();
  const v = syncView(sync, online);
  const Icon = ICONS[v.key];
  const label =
    v.key === 'saved'
      ? t('annotate.sync_saved')
      : v.key === 'offline'
        ? t('annotate.sync_offline')
        : v.key === 'error'
          ? t('annotate.sync_error')
          : v.key === 'saving'
            ? t('annotate.sync_saving')
            : t('annotate.sync_pending', { count: v.count });
  return (
    <button
      type="button"
      title={iconOnly ? `${label} · ${t('annotate.sync_tap')}` : t('annotate.sync_tap')}
      onClick={() => {
        sync.flush().catch(() => undefined);
      }}
      className={cx(
        'inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-2 text-[14px] font-semibold whitespace-nowrap transition hover:bg-[var(--surface-2)]',
        v.key === 'saved' ? 'text-[var(--ok-text)]' : v.key === 'error' ? 'text-[var(--danger-text)]' : 'text-[var(--warn-text)]',
      )}
    >
      <Icon className={cx('size-5 shrink-0', v.key === 'saving' && 'animate-spin')} aria-hidden />
      <span aria-live="polite" className={iconOnly ? 'sr-only' : compact ? 'sr-only sm:not-sr-only' : undefined}>
        {label}
      </span>
    </button>
  );
}
